/**
 * Real-world terrain grids (elevation + ground classes) built by scripts/terrain/*.mjs and served from
 * public/terrain/<name>.{json,bin}. Loading is async (fetch, or fs in tests); after that every lookup is synchronous
 * and deterministic, so the sim, the chunk workers and the tests all see the same ground.
 */

export const TERRAIN_CLASSES = ['forest', 'grass', 'heath', 'dirt', 'sand', 'road', 'water'] as const;
export type TerrainClass = (typeof TERRAIN_CLASSES)[number];

export interface TerrainMeta {
  name: string;
  cell: number;     // metres between samples
  nx: number;       // samples along x
  nz: number;       // samples along z
  originX: number;  // game x of sample (0, 0)
  originZ: number;  // game z of sample (0, 0)
  min: number;      // height of stored value 0 (metres above datum)
  datum: number;    // metres above sea level of game height 0
  classes: readonly string[];
}

/** Heights (bilinear) and ground classes (nearest sample) on a regular grid; clamps outside it. */
export class TerrainGrid {
  constructor(readonly meta: TerrainMeta, readonly heights: Float32Array, readonly classes: Uint8Array) {
    if (heights.length !== meta.nx * meta.nz || classes.length !== meta.nx * meta.nz) throw new Error(`terrain ${meta.name}: size mismatch`);
    TERRAIN_CLASSES.forEach((c, i) => {
      if (meta.classes[i] !== c) throw new Error(`terrain ${meta.name}: class ${i} is ${meta.classes[i]}, expected ${c}`);
    });
  }

  heightAt(x: number, z: number): number {
    const { cell, nx, nz, originX, originZ } = this.meta;
    const fx = Math.min(nx - 1, Math.max(0, (x - originX) / cell));
    const fz = Math.min(nz - 1, Math.max(0, (z - originZ) / cell));
    const i = Math.min(Math.floor(fx), nx - 2), j = Math.min(Math.floor(fz), nz - 2);
    const tx = fx - i, tz = fz - j, h = this.heights, k = j * nx + i;
    return (h[k]! * (1 - tx) + h[k + 1]! * tx) * (1 - tz) + (h[k + nx]! * (1 - tx) + h[k + nx + 1]! * tx) * tz;
  }

  /** Index into TERRAIN_CLASSES of the nearest sample. */
  classAt(x: number, z: number): number {
    const { cell, nx, nz, originX, originZ } = this.meta;
    const i = Math.min(nx - 1, Math.max(0, Math.round((x - originX) / cell)));
    const j = Math.min(nz - 1, Math.max(0, Math.round((z - originZ) / cell)));
    return this.classes[j * nx + i]!;
  }
}

/** Decodes `bin` (Uint16 LE heights in cm above meta.min, then Uint8 classes). */
export function parseTerrain(meta: TerrainMeta, bin: ArrayBuffer | Uint8Array): TerrainGrid {
  const bytes = bin instanceof Uint8Array ? bin : new Uint8Array(bin);
  const n = meta.nx * meta.nz;
  if (bytes.length !== 3 * n) throw new Error(`terrain ${meta.name}: expected ${3 * n} bytes, got ${bytes.length}`);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const heights = new Float32Array(n);
  for (let k = 0; k < n; k++) heights[k] = meta.min + view.getUint16(2 * k, true) / 100;
  return new TerrainGrid(meta, heights, bytes.slice(2 * n, 3 * n));
}

const grids = new Map<string, TerrainGrid>();
const pending = new Map<string, Promise<TerrainGrid>>();

export function registerTerrain(grid: TerrainGrid): void { grids.set(grid.meta.name, grid); }
export const hasTerrain = (name: string): boolean => grids.has(name);

export function getTerrain(name: string): TerrainGrid {
  const g = grids.get(name);
  if (!g) throw new Error(`terrain ${name} is not loaded: await loadTerrain('${name}') first`);
  return g;
}

/** Fetches and registers public/terrain/<name>.{json,bin} once; `base` is the site root (import.meta.env.BASE_URL). */
export function loadTerrain(name: string, base = '/'): Promise<TerrainGrid> {
  const hit = grids.get(name);
  if (hit) return Promise.resolve(hit);
  let p = pending.get(name);
  if (!p) {
    const url = (ext: string) => `${base.endsWith('/') ? base : `${base}/`}terrain/${name}.${ext}`;
    p = (async () => {
      const [meta, bin] = await Promise.all([
        fetch(url('json')).then((r) => { if (!r.ok) throw new Error(`${url('json')}: HTTP ${r.status}`); return r.json() as Promise<TerrainMeta>; }),
        fetch(url('bin')).then((r) => { if (!r.ok) throw new Error(`${url('bin')}: HTTP ${r.status}`); return r.arrayBuffer(); }),
      ]);
      const grid = parseTerrain(meta, bin);
      registerTerrain(grid);
      return grid;
    })();
    pending.set(name, p);
    p.catch(() => pending.delete(name));
  }
  return p;
}
